import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router';

import { SoftwareOnboardingFooter, StepSubmitButton } from 'src/components/SoftwareOnboarding';
import { Button } from 'src/components/ui/button';
import { useSoftwareEscrowOnboarding, useSoftwareEscrow } from 'src/context/software-escrow';
import { AgreementGenerateDialog, AgreementReviewDialog } from 'src/containers/SoftwareEscrow/Agreement';

type IFooterButton = {
  onClick: () => void;
  variant?: 'default' | 'outline';
  children: React.ReactNode;
};

const FooterButton = ({ onClick, variant, children }: IFooterButton) => (
  <Button onClick={onClick} variant={variant}>
    {children}
  </Button>
);

const BackButton = ({ onClick }: { onClick: () => void }) => (
  <FooterButton variant="outline" onClick={onClick}>
    <ArrowLeft /> Back
  </FooterButton>
);

const NextButton = ({ onClick }: Pick<IFooterButton, 'onClick'>) => (
  <StepSubmitButton onSuccess={onClick}>
    Next <ArrowRight />
  </StepSubmitButton>
);

const EditModeFooter = ({ id }: { id: string }) => {
  const navigate = useNavigate();
  const goBackToSummary = () => navigate(`/software-escrows/onboarding/${id}/summary`);

  return (
    <SoftwareOnboardingFooter justify="end">
      <StepSubmitButton onSuccess={goBackToSummary}>
        Back to summary <ArrowRight />
      </StepSubmitButton>
    </SoftwareOnboardingFooter>
  );
};

const AgreementButtons = ({ isInvitedParty }: { isInvitedParty: boolean }) => {
  if (isInvitedParty) return <AgreementReviewDialog triggerText="Submit" />;

  return <AgreementGenerateDialog triggerText="Generate Agreement" />;
};

const Footer = () => {
  const { handlePrevStep, handleNextStep, totalSteps, currentStep, showBackButton } = useSoftwareEscrowOnboarding();
  const [searchParams] = useSearchParams();
  const {
    softwareEscrow: { id, currentCompanyRole, startedBy },
  } = useSoftwareEscrow();

  const isEdit = searchParams.get('isEdit') === 'true';
  if (isEdit) return <EditModeFooter id={id} />;

  const isInvitedParty = currentCompanyRole !== startedBy;

  return (
    <SoftwareOnboardingFooter justify={showBackButton ? 'between' : 'end'}>
      {showBackButton && <BackButton onClick={handlePrevStep} />}
      {currentStep === totalSteps ? (
        <AgreementButtons isInvitedParty={isInvitedParty} />
      ) : (
        <NextButton onClick={handleNextStep} />
      )}
    </SoftwareOnboardingFooter>
  );
};

export default Footer;
